import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { addChangeOfVuenueFeedback as addChangeOfVenueFeedback } from "services/caseServices";
import {
  ChangeOfVenue,
  Roles,
  VenueFeedback,
  VenueFeedbackAction,
} from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  venue: ChangeOfVenue;
  venue_feedbacks: VenueFeedback[];
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const ChangeOfVenueFeedback: React.FC<Props> = ({
  venue,
  venue_feedbacks,
  setRefetch,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const [newComment, setNewComment] = React.useState<string>("");
  const [action, setAction] = React.useState<string>("");

  return (
    <div>
      <h1 className="text-black font-semibold text-lg flex flex-row gap-2 items-center mb-2">
        <span className="text-[#AA232F] text-xl">{feedbackIcon}</span>
        <span className="tracking-wide">Feedback Section</span>
      </h1>
      <div className="flex flex-col gap-4 mt-2 justify-between h-[60vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-3 pr-1">
          {venue_feedbacks && venue_feedbacks.length > 0 ? (
            venue_feedbacks?.map((feedback, index) => (
              <div
                key={index}
                className={`flex flex-row items-start gap-3 rounded-lg shadow-sm border border-gray-200 px-4 py-3 ${
                  index % 2 === 0 ? "bg-[#F7F7F7]" : "bg-white"
                }`}
                style={{ overflowWrap: "break-word" }}
              >
                <div className="flex-1">
                  <div className="flex flex-row gap-2 items-center mb-1">
                    <span className="text-xs text-gray-800"></span>
                  </div>
                  <div className="flex flex-row gap-4 mb-1">
                    <span className="text-xs text-gray-600 font-semibold">
                      Action:
                    </span>
                    <span className="text-xs text-gray-800">
                      {feedback.action || "-"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-1">
                    {feedback.comment}
                  </p>
                  <div className="flex flex-row gap-4 mt-1">
                    <span className="text-[10px] text-gray-500 capitalize">
                      By: {feedback.created_by}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-800 py-8">
              No feedback yet.
            </div>
          )}
        </div>

        {isCoordinator && venue?.status === "pending" && (
          <div
            style={{ boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px" }}
            className="px-6 py-4 w-full bg-white rounded-lg border border-gray-200 mt-2"
          >
            <p className="font-semibold text-[#AA232F] mb-2">
              {/* {statusLabels["respond_back"]} */}
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Decision Type
                </label>
                <select
                  className="w-full bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-[#AA232F] text-sm resize-none"
                  name="decision_type"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  <option value={""}>Select Action</option>
                  {Object.entries(VenueFeedbackAction).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                rows={4}
                className="w-full bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AA232F] text-sm resize-none"
                placeholder="Write your feedback here..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="text-right mt-2 gap-4">
                <button
                  className="px-5 py-2 rounded bg-[#AA232F] text-white text-xs font-semibold shadow hover:bg-[#E90101] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#AA232F]"
                  onClick={() => {
                    if (!action.trim()) {
                      showTooltip(t("please_select_an_action"), "error");
                      return;
                    }
                    if (!newComment.trim()) {
                      showTooltip(t("please_enter_a_comment"), "error");
                      return;
                    }

                    addChangeOfVenueFeedback({
                      action: action,
                      comment: newComment,
                      created_by: loginData?.full_name,
                      change_of_venue: venue?.id,
                    })
                      .then(() => {
                        setNewComment("");
                        setRefetch((prev: any) => !prev);
                        showTooltip(t("comment_added_successfully"), "success");
                      })
                      .catch((error) => {
                        showTooltip(
                          errorHandlerOnForm(error) || "Error adding comment:",
                          "error",
                        );
                      });
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeOfVenueFeedback;
